// Styles
import '@mdi/font/css/materialdesignicons.css';
import 'vuetify/styles';
import '@/styles/md3.css';

// Vuetify
import { createVuetify, type ThemeDefinition } from 'vuetify';
import * as components from 'vuetify/components';
import * as directives from 'vuetify/directives';
import { md3 } from 'vuetify/blueprints';

// Material 3 colour roles, generated with Google's material-color-utilities
// (as Material Theme Builder does) from the brand colours #006e18 (primary)
// and #006a63 (secondary); the other roles follow from the primary.
// Vuetify's own names map onto them: cards and sheets ('surface') use
// surface-container-low on the page ('background', the M3 surface), and
// 'surface-variant' is Vuetify's inverse surface behind snackbars and
// tooltips, so it holds M3 inverse-surface.
const light: ThemeDefinition = {
  dark: false,
  colors: {
    background: '#f7fbf1',
    'on-background': '#191d17',
    surface: '#f1f5eb',
    'on-surface': '#191d17',
    'surface-bright': '#f7fbf1',
    'surface-light': '#e0e4da',
    'surface-variant': '#2d322b',
    'on-surface-variant': '#eff2e9',
    primary: '#006e18',
    'on-primary': '#ffffff',
    'primary-container': '#99f991',
    'on-primary-container': '#005310',
    secondary: '#006a63',
    'on-secondary': '#ffffff',
    'secondary-container': '#a0f1e8',
    'on-secondary-container': '#00504a',
    tertiary: '#38656a',
    'on-tertiary': '#ffffff',
    'tertiary-container': '#bcebf0',
    'on-tertiary-container': '#1e4d52',
    error: '#ba1a1a',
    'on-error': '#ffffff',
    'error-container': '#ffdad6',
    'on-error-container': '#93000a',
    'surface-container-lowest': '#ffffff',
    'surface-container-low': '#f1f5eb',
    'surface-container': '#ecefe6',
    'surface-container-high': '#e6e9e0',
    'surface-container-highest': '#e0e4da',
    outline: '#73796f',
    'outline-variant': '#c2c8bd',
    'inverse-primary': '#7ddc78',
  },
  variables: {
    // Vuetify's default 0.6 gives 4.27:1 on the surface; WCAG AA needs 4.5:1
    'medium-emphasis-opacity': 0.7,
  },
};

const dark: ThemeDefinition = {
  dark: true,
  colors: {
    background: '#10140f',
    'on-background': '#e0e4da',
    surface: '#191d17',
    'on-surface': '#e0e4da',
    'surface-bright': '#363a34',
    'surface-light': '#323630',
    'surface-variant': '#e0e4da',
    'on-surface-variant': '#2d322b',
    primary: '#7ddc78',
    'on-primary': '#003908',
    'primary-container': '#005310',
    'on-primary-container': '#99f991',
    secondary: '#84d5cc',
    'on-secondary': '#003733',
    'secondary-container': '#00504a',
    'on-secondary-container': '#a0f1e8',
    tertiary: '#a0cfd4',
    'on-tertiary': '#00363b',
    'tertiary-container': '#1e4d52',
    'on-tertiary-container': '#bcebf0',
    error: '#ffb4ab',
    'on-error': '#690005',
    'error-container': '#93000a',
    'on-error-container': '#ffdad6',
    'surface-container-lowest': '#0b0f0a',
    'surface-container-low': '#191d17',
    'surface-container': '#1d211b',
    'surface-container-high': '#272b25',
    'surface-container-highest': '#323630',
    outline: '#8c9388',
    'outline-variant': '#424940',
    'inverse-primary': '#006e18',
  },
};

export default createVuetify({
  components,
  directives,
  blueprint: md3,
  theme: {
    themes: {
      light,
      dark,
    },
  },
  // Material 3 components, on top of the md3 blueprint
  defaults: {
    VTextField: { variant: 'outlined' },
    VTextarea: { variant: 'outlined' },
    // Small shape (8px)
    VChip: { rounded: 'lg' },
    // Snackbars sit on the inverse surface; their actions use inverse-primary
    VSnackbar: { VBtn: { color: 'inverse-primary' } },
  },
  display: {
    thresholds: {
      md: 960,
      lg: 1280,
      xl: 1920,
      xxl: 2560,
    },
  },
});
